import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function SetSupplierAgreementPriceDialog({
  open,
  submitting,
  materialOptions,
  initialMaterialId,
  onClose,
  onSubmit,
}: {
  open: boolean;
  submitting: boolean;
  materialOptions: { value: string; label: string }[];
  initialMaterialId?: string;
  onClose: () => void;
  onSubmit: (input: { material_id: string; price: string; effective_date: string }) => void;
}) {
  const { t } = useTranslation();
  const [materialId, setMaterialId] = useState("");
  const [price, setPrice] = useState("");
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(() => new Date());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMaterialId(initialMaterialId ?? "");
      setPrice("");
      setEffectiveDate(new Date());
      setError(null);
    }
  }, [open, initialMaterialId]);

  const handleSubmit = () => {
    const numericPrice = Number(price);
    if (!materialId) {
      setError(t("suppliers.details.agreement.prices.materialPlaceholder"));
      return;
    }
    if (!price || Number.isNaN(numericPrice) || numericPrice <= 0) {
      setError(t("suppliers.details.agreement.prices.price"));
      return;
    }
    if (!effectiveDate || Number.isNaN(effectiveDate.getTime())) {
      setError(t("suppliers.details.agreement.prices.effectiveDate"));
      return;
    }
    setError(null);
    onSubmit({ material_id: materialId, price, effective_date: toIsoDate(effectiveDate) });
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t("suppliers.details.agreement.prices.setPrice")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            select
            fullWidth
            required
            label={t("suppliers.details.agreement.prices.material")}
            value={materialId}
            onChange={(e) => setMaterialId(e.target.value)}
            disabled={submitting || !!initialMaterialId}
            SelectProps={{
              renderValue: (value) => {
                const option = materialOptions.find((o) => o.value === value);
                return option ? `${option.label} (${option.value})` : String(value ?? "");
              },
            }}
          >
            {materialOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    {option.value}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            required
            type="number"
            label={t("suppliers.details.agreement.prices.price")}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            disabled={submitting}
            InputProps={{ startAdornment: <InputAdornment position="start">SAR</InputAdornment> }}
            inputProps={{ min: 0, step: "0.01" }}
          />
          <DatePicker
            label={t("suppliers.details.agreement.prices.effectiveDate")}
            value={effectiveDate}
            onChange={(value) => setEffectiveDate(value)}
            disabled={submitting}
            slotProps={{ textField: { fullWidth: true, required: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t("suppliers.form.cancel")}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? t("suppliers.details.agreement.prices.saving") : t("suppliers.details.agreement.prices.setPrice")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
